import { isNewerKioskReleaseName } from "@teamtable/api";
import { useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "../api/client.js";
import {
  describeUpdateStatus,
  type DeviceKioskUpdate as UpdateRow,
  formatWhen,
  type KioskRelease,
  OPEN_UPDATE_STATUSES,
  updateStatusClasses,
} from "../lib/kioskReleases.js";

/**
 * Per-table kiosk app update. Platform-admin only — the caller gates rendering
 * and every endpoint underneath is `requirePlatformAdmin`.
 *
 * The table decides when to install: it downloads in the background, then asks
 * the platform for permission on each heartbeat and is refused while a session
 * is live. So "staged" can sit for as long as the table is busy, and that is
 * the mechanism working, not a stall. Success is only ever inferred from the
 * new build's first heartbeat, which is also what lifts the payment hold.
 */
export function DeviceKioskUpdate({
  deviceId,
  deviceName,
  installedVersionCode,
  installedVersionName,
  onInstalledVersionChanged,
}: {
  deviceId: string;
  deviceName: string;
  installedVersionCode?: number;
  installedVersionName?: string;
  /** Called when an open update closes, so the caller can refetch the device's version. */
  onInstalledVersionChanged: () => void;
}) {
  const [releases, setReleases] = useState<KioskRelease[]>([]);
  const [updates, setUpdates] = useState<UpdateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReleaseId, setSelectedReleaseId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const open = useMemo(() => updates.find((u) => OPEN_UPDATE_STATUSES.has(u.status)), [updates]);
  const history = useMemo(() => updates.filter((u) => u.id !== open?.id), [updates, open]);

  const fetchAll = useCallback(async () => {
    try {
      const [releasesRes, updatesRes] = await Promise.all([
        apiClient["/api/v1/admin/kiosk-releases"]["get"]({ parameters: {}, body: null }),
        apiClient["/api/v1/admin/devices/{id}/kiosk-updates"]["get"]({
          parameters: { id: deviceId },
          body: null,
        }),
      ]);
      if (releasesRes.code === 200) setReleases(releasesRes.body.releases as KioskRelease[]);
      if (updatesRes.code === 200) setUpdates(updatesRes.body as UpdateRow[]);
    } catch {
      // Leave the last good state up; a failed poll is not worth blanking the section for.
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // Poll while something is in flight, and tell the parent when it closes so
  // the installed version in the device info refreshes.
  const openId = open?.id;
  useEffect(() => {
    if (!openId) return;
    const t = setInterval(() => void fetchAll(), 5000);
    return () => clearInterval(t);
  }, [openId, fetchAll]);

  const [lastOpenId, setLastOpenId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (lastOpenId && !openId) onInstalledVersionChanged();
    setLastOpenId(openId);
  }, [openId, lastOpenId, onInstalledVersionChanged]);

  const eligible = useMemo(
    () =>
      releases.filter(
        (r) =>
          isNewerKioskReleaseName(r.version_name, installedVersionName) &&
          (installedVersionCode === undefined || r.version_code > installedVersionCode),
      ),
    [releases, installedVersionCode, installedVersionName],
  );
  const selected = eligible.find((r) => r.id === selectedReleaseId);

  const target = async () => {
    if (!selected) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/kiosk-updates"]["post"]({
        parameters: { id: deviceId },
        body: { release_id: selected.id },
      });
      if (res.code === 201) {
        setMessage({
          type: "success",
          text: `${selected.version_name} queued. The table downloads it on its next heartbeat and installs at startup or scheduled downtime.`,
        });
        setSelectedReleaseId("");
        setConfirming(false);
        await fetchAll();
      } else {
        const body = res.body as { message?: string };
        setMessage({
          type: "error",
          text: body.message ?? `Could not queue the update (${res.code}).`,
        });
      }
    } catch {
      setMessage({ type: "error", text: "Could not reach the server to queue the update." });
    } finally {
      setBusy(false);
    }
  };

  const forceUpdate = async () => {
    if (
      !open ||
      !window.confirm(
        `Update ${deviceName} to ${open.version_name} as soon as possible? This bypasses the schedule and waits for any live session to finish.`,
      )
    )
      return;
    setBusy(true);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/kiosk-updates/{updateId}/force"].post(
        { parameters: { id: deviceId, updateId: open.id }, body: null },
      );
      setMessage(
        res.code === 200
          ? {
              type: "success",
              text: "Force update requested. Waiting for the table to be online and available.",
            }
          : { type: "error", text: (res.body as { message?: string }).message ?? "Request failed" },
      );
      await fetchAll();
    } catch {
      setMessage({ type: "error", text: "Could not reach the server." });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!open) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/kiosk-updates/{updateId}/cancel"][
        "post"
      ]({
        parameters: { id: deviceId, updateId: open.id },
        body: null,
      });
      if (res.code === 200) {
        setMessage({ type: "success", text: "Update cancelled." });
        await fetchAll();
      } else {
        const body = res.body as { message?: string };
        setMessage({ type: "error", text: body.message ?? `Could not cancel (${res.code}).` });
      }
    } catch {
      setMessage({ type: "error", text: "Could not reach the server to cancel." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-surface-raised border border-border rounded-lg p-6 mb-6"
      data-testid="device-kiosk-update"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-text-primary">📲 Kiosk App</h2>
        <button
          onClick={() => void fetchAll()}
          className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
        >
          Refresh
        </button>
      </div>

      <dl className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <dt className="text-xs text-text-muted">Installed</dt>
          <dd className="text-sm text-text-secondary font-mono">
            {installedVersionName ?? "—"}
            {installedVersionCode !== undefined && (
              <span className="text-text-muted"> (code {installedVersionCode})</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-text-muted">Newest release</dt>
          <dd className="text-sm text-text-secondary font-mono">
            {releases[0] ? `${releases[0].version_name} (code ${releases[0].version_code})` : "—"}
          </dd>
        </div>
      </dl>

      {loading ? (
        <p className="text-sm text-text-secondary">Loading...</p>
      ) : open ? (
        <div
          className="p-4 rounded-md border border-yellow-500/50 bg-yellow-500/10"
          data-testid="kiosk-update-open"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-text-primary font-medium">
              Updating to {open.version_name}{" "}
              <span className="text-xs text-text-muted font-mono">code {open.version_code}</span>
            </p>
            <span className={`text-sm font-medium ${updateStatusClasses(open.status)}`}>
              {open.status}
            </span>
          </div>
          <p className="text-xs text-text-secondary mt-1">{describeUpdateStatus(open.status)}</p>
          <dl className="text-xs text-text-secondary grid grid-cols-3 gap-2 mt-3">
            <div>
              <dt>Queued</dt>
              <dd className="text-text-primary">{formatWhen(open.created_at)}</dd>
            </div>
            <div>
              <dt>Last change</dt>
              <dd className="text-text-primary">{formatWhen(open.updated_at)}</dd>
            </div>
            <div>
              <dt>Install authorised</dt>
              <dd className="text-text-primary">{formatWhen(open.install_started_at)}</dd>
            </div>
          </dl>
          <button
            onClick={() => void cancel()}
            disabled={busy}
            className="mt-3 px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
          >
            Cancel update
          </button>
        </div>
      ) : releases.length === 0 ? (
        <p className="text-sm text-text-secondary">
          No kiosk releases have been uploaded. Upload one on the Kiosk page first.
        </p>
      ) : eligible.length === 0 ? (
        <p className="text-sm text-text-secondary">This table already runs the newest release.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedReleaseId}
            onChange={(e) => {
              setSelectedReleaseId(e.target.value);
              setConfirming(false);
              setMessage(null);
            }}
            aria-label="Kiosk release"
            disabled={busy}
            className="px-3 py-2 rounded-md bg-surface border border-border text-text-primary text-sm"
          >
            <option value="">Choose a release…</option>
            {eligible.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version_name} (code {r.version_code})
              </option>
            ))}
          </select>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={!selected || busy}
              className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update this table
            </button>
          ) : (
            <div className="w-full mt-1 p-4 rounded-md border border-yellow-500/50 bg-yellow-500/10">
              <p className="text-sm text-text-primary font-medium mb-1">
                Update {deviceName} to {selected?.version_name}?
              </p>
              <p className="text-xs text-text-secondary mb-3">
                The table downloads now and installs at startup or the start of scheduled downtime.
                Payments pause during installation and management verification. A failed
                verification requires recovery; Android rollback is not guaranteed for every
                failure.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => void target()}
                  disabled={busy}
                  className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {busy ? "Queueing…" : "Confirm update"}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {open &&
        !["installing", "verifying"].includes(open.status) &&
        open.scheduling_mode !== "asap" && (
          <button
            onClick={() => void forceUpdate()}
            disabled={busy}
            className="mt-3 px-4 py-2 rounded-md border border-border text-sm"
          >
            Force update now
          </button>
        )}
      {open?.scheduling_mode === "asap" && (
        <p className="mt-2 text-sm text-text-secondary">
          Force update requested — installs as soon as online and no live session remains.
        </p>
      )}
      {message && (
        <p
          className={`text-sm mt-3 ${message.type === "success" ? "text-green-400" : "text-red-400"}`}
        >
          {message.text}
        </p>
      )}

      {history.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <h3 className="text-sm font-medium text-text-secondary mb-2">History</h3>
          <div className="space-y-2">
            {history.map((u) => (
              <div
                key={u.id}
                className="bg-surface border border-border rounded-md px-3 py-2 text-sm flex items-start justify-between gap-3"
              >
                <div>
                  <span className="text-text-primary">{u.version_name}</span>{" "}
                  <span className="text-xs text-text-muted font-mono">code {u.version_code}</span>
                  {u.from_version_code !== undefined && (
                    <span className="text-xs text-text-muted">
                      {" "}
                      from code {u.from_version_code}
                    </span>
                  )}
                  {u.forced_at && (
                    <p className="text-xs text-text-muted">
                      Forced {formatWhen(u.forced_at)} · requester {u.forced_by}
                    </p>
                  )}
                  {u.install_trigger && (
                    <p className="text-xs text-text-muted">Trigger: {u.install_trigger}</p>
                  )}
                  {u.detail && <p className="text-xs text-text-secondary mt-1">{u.detail}</p>}
                  {u.requested_by_email && (
                    <p className="text-xs text-text-muted mt-1">
                      Requested by {u.requested_by_email}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-medium ${updateStatusClasses(u.status)}`}>
                    {u.status}
                  </div>
                  <div className="text-xs text-text-muted">
                    {formatWhen(u.completed_at ?? u.updated_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
